import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Brain, AlertTriangle, CheckCircle2, Clock, MessageSquare, FileText } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";

// STM defaults (espelham _shared/memory-budget.ts)
const STM_DEFAULTS = {
  max_messages: 300,
  max_chars: 24_000,
  max_age_hours: 48,
  min_messages: 8,
} as const;

interface StmFlags {
  max_messages: number;
  max_chars: number;
  max_age_hours: number;
  min_messages: number;
}

interface StarvedAggregate {
  total_used: number;
  total_starved: number;
  starved_pct: number;
  models_starved: { model: string; count: number }[];
  last_starved_at: string | null;
  avg_chars_used: number | null;
}

async function fetchStmFlags(): Promise<StmFlags> {
  const keys = [
    "memory.stm.max_messages",
    "memory.stm.max_chars",
    "memory.stm.max_age_hours",
    "memory.stm.min_messages",
  ];
  const { data, error } = await supabase
    .from("cortex_feature_flags")
    .select("flag_key, enabled, payload")
    .in("flag_key", keys);

  if (error || !Array.isArray(data)) return { ...STM_DEFAULTS };

  const numFromRow = (row: any, fallback: number): number => {
    if (!row?.enabled) return fallback;
    const raw = row?.payload?.value;
    const n = typeof raw === "number" ? raw : Number(raw);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : fallback;
  };

  const byKey = new Map<string, any>();
  for (const row of data) byKey.set(row.flag_key, row);

  return {
    max_messages: numFromRow(byKey.get("memory.stm.max_messages"), STM_DEFAULTS.max_messages),
    max_chars: numFromRow(byKey.get("memory.stm.max_chars"), STM_DEFAULTS.max_chars),
    max_age_hours: numFromRow(byKey.get("memory.stm.max_age_hours"), STM_DEFAULTS.max_age_hours),
    min_messages: numFromRow(byKey.get("memory.stm.min_messages"), STM_DEFAULTS.min_messages),
  };
}

async function fetchStarvedAggregate(): Promise<StarvedAggregate> {
  const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

  const [usedRes, starvedRes] = await Promise.all([
    supabase
      .from("cortex_events")
      .select("payload, occurred_at")
      .eq("event_type", "memory.stm.budget_used")
      .gte("occurred_at", since)
      .limit(1000),
    supabase
      .from("cortex_events")
      .select("payload, occurred_at")
      .eq("event_type", "memory.stm.budget_starved")
      .gte("occurred_at", since)
      .order("occurred_at", { ascending: false })
      .limit(500),
  ]);

  const usedRows = (usedRes.data ?? []) as any[];
  const starvedRows = (starvedRes.data ?? []) as any[];

  const total_used = usedRows.length;
  const total_starved = starvedRows.length;
  const starved_pct = total_used > 0 ? Math.round((total_starved / total_used) * 100) : 0;

  const charsValues = usedRows
    .map((r) => Number(r?.payload?.chars_used))
    .filter((n) => Number.isFinite(n) && n > 0);
  const avg_chars_used =
    charsValues.length > 0
      ? Math.round(charsValues.reduce((a, b) => a + b, 0) / charsValues.length)
      : null;

  const modelCounts = new Map<string, number>();
  for (const row of starvedRows) {
    const model = (row?.payload?.source_model ?? "unknown") as string;
    modelCounts.set(model, (modelCounts.get(model) ?? 0) + 1);
  }
  const models_starved = Array.from(modelCounts.entries())
    .map(([model, count]) => ({ model, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 3);

  const last_starved_at = starvedRows[0]?.occurred_at ?? null;

  return { total_used, total_starved, starved_pct, models_starved, last_starved_at, avg_chars_used };
}

function fmtChars(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return String(n);
}

export function StmBudgetWidget() {
  const { data: flags, isLoading: loadingFlags } = useQuery({
    queryKey: ["stm-flags"],
    queryFn: fetchStmFlags,
    refetchInterval: 60_000,
  });
  const { data: agg, isLoading: loadingAgg } = useQuery({
    queryKey: ["stm-starved-aggregate"],
    queryFn: fetchStarvedAggregate,
    refetchInterval: 60_000,
  });

  const loading = loadingFlags || loadingAgg;
  const isStarved = (agg?.total_starved ?? 0) > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    >
      <Card className="border-border/60">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Brain className="h-4 w-4 text-primary" />
              Memória de Curto Prazo (STM)
            </CardTitle>
            {!loading &&
              (isStarved ? (
                <Badge variant="destructive" className="gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Budget starved
                </Badge>
              ) : (
                <Badge variant="outline" className="gap-1 text-primary border-primary/30">
                  <CheckCircle2 className="h-3 w-3" />
                  Saudável
                </Badge>
              ))}
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Limites configurados */}
          <div className="grid grid-cols-3 gap-3">
            <StmStat
              icon={<FileText className="h-3.5 w-3.5" />}
              label="max_chars"
              value={loading ? null : fmtChars(flags?.max_chars ?? 0)}
              hint="caracteres"
            />
            <StmStat
              icon={<MessageSquare className="h-3.5 w-3.5" />}
              label="max_messages"
              value={loading ? null : String(flags?.max_messages ?? "-")}
              hint="mensagens"
            />
            <StmStat
              icon={<Clock className="h-3.5 w-3.5" />}
              label="max_age"
              value={loading ? null : `${flags?.max_age_hours ?? "-"}h`}
              hint="janela"
            />
          </div>

          {/* Uso real (24h) */}
          <div className="rounded-lg border border-border/50 bg-muted/30 p-3 space-y-2">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Uso real (últimas 24h)</span>
              <span>
                {loading ? "..." : `${agg?.total_used ?? 0} turnos`}
              </span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-semibold tabular-nums">
                {loading || agg?.avg_chars_used == null ? "—" : fmtChars(agg.avg_chars_used)}
              </span>
              <span className="text-xs text-muted-foreground">
                média de chars / turno
              </span>
            </div>
          </div>

          {/* Alerta starved */}
          {!loading && isStarved && (
            <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 space-y-2">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                <div className="space-y-1 flex-1">
                  <p className="text-sm font-medium text-destructive">
                    STM truncada por modelo desconhecido
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {agg!.total_starved} eventos nas últimas 24h ({agg!.starved_pct}% dos turnos).
                    Adicione o modelo à whitelist em{" "}
                    <code className="text-[11px] bg-muted px-1 py-0.5 rounded">
                      _shared/memory-budget.ts
                    </code>
                    .
                  </p>
                </div>
              </div>
              {agg!.models_starved.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {agg!.models_starved.map((m) => (
                    <Badge key={m.model} variant="outline" className="text-[11px] font-mono">
                      {m.model} · {m.count}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}

          {!loading && !isStarved && (
            <p className="text-xs text-muted-foreground">
              Nenhum evento <code className="text-[11px] bg-muted px-1 rounded">budget_starved</code>{" "}
              nas últimas 24h. Todos os modelos em uso estão na whitelist.
            </p>
          )}

          <div className="pt-1">
            <a
              href="/docs/stm-budget"
              className="text-[11px] text-primary hover:underline"
            >
              Como funciona o STM Budget e a regra dos 5% →
            </a>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function StmStat({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | null;
  hint: string;
}) {
  return (
    <div className="rounded-lg border border-border/50 bg-card p-3">
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground font-mono">
        {icon}
        {label}
      </div>
      <div className="mt-1 text-lg font-semibold tabular-nums">
        {value ?? <Skeleton className="h-6 w-12" />}
      </div>
      <div className="text-[11px] text-muted-foreground">{hint}</div>
    </div>
  );
}
